import { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { apiGet } from '@services/api';
import { getImageUrl } from '@utils/imageUrl';
import { normalizeStoredImageRef } from '@utils/profilePicture';
import { resendVerificationEmail } from '@services/emailVerification';
import { useI18n } from '../i18n';
import { getSkillLabel } from '@utils/skillLabels';

const fallbackProfile = {
	joined: 'March 4, 2024',
	location: 'Portland, OR',
	bio: 'Passionate about sustainable landscapes and empowering communities to grow thriving green spaces.',
	interests: ['Urban farming', 'Native plants', 'Community outreach']
};

export default function Profile() {
	const { user, logout, refreshUser } = useAuth();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const navigate = useNavigate();
	const [profileData, setProfileData] = useState<any>(null);
	const [loading, setLoading] = useState(true);
	const [profileImageFailed, setProfileImageFailed] = useState(false);
	const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
	const [resendMessage, setResendMessage] = useState<string | null>(null);

	// Load profile data
	useEffect(() => {
		if (!user?.id) {
			setLoading(false);
			return;
		}

		const loadProfile = async () => {
			try {
				const data = await apiGet<{ profile: any }>(`/users/me/profile?userId=${user.id}`);
				setProfileData(data.profile);
			} catch (error) {
				console.error('Error loading profile:', error);
			} finally {
				setLoading(false);
			}
		};

		loadProfile();
	}, [user?.id]);

	// Profile API knows verification before in-memory auth (e.g. after clicking email link). Sync topbar.
	useEffect(() => {
		if (!user?.id || !profileData) return;
		const verified =
			profileData.email_verified === true ||
			profileData.email_verified === 1 ||
			!!profileData.email_verified_at;
		if (verified && user.emailVerified !== true) {
			void refreshUser(user.id);
		}
	}, [profileData, user?.id, user?.emailVerified, refreshUser]);

	// Reset image-failed state when profile picture URL changes
	useEffect(() => {
		setProfileImageFailed(false);
	}, [profileData?.profile_picture_url]);

	const profile = useMemo(() => {
		if (!user) {
			return {
				name: 'HortEra User',
				email: 'user@hortera.local',
				role: copy('Member', 'Mitglied', 'Član'),
				...fallbackProfile
			};
		}

		const gardenerLabel =
			user.role === 'gardener' && user.gardenerType === 'freelance'
				? copy('Freelance Gardener', 'Freiberuflicher Gärtner', 'Samostalni vrtlar')
				: user.role === 'gardener'
					? copy('Gardener', 'Gärtner', 'Vrtlar')
					: user.role === 'company'
						? copy('Company', 'Unternehmen', 'Tvrtka')
						: user.role === 'admin'
							? copy('Administrator', 'Administrator', 'Administrator')
							: user.role === 'seeker'
								? copy('Garden Seeker', 'Gartensuchende', 'Vlasnik vrta')
								: copy('Member', 'Mitglied', 'Član');

		// Format joined date
		const joinedDate = profileData?.created_at 
			? new Date(profileData.created_at).toLocaleDateString(language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })
			: fallbackProfile.joined;

		return {
			name: user.name,
			email: user.email,
			role: gardenerLabel,
			joined: joinedDate,
			location: ((profileData?.location || user.companyName || copy('Portland, USA', 'Portland, USA', 'Portland, SAD')) as string)
				.replace(/,\s*Croatia$/i, language === 'de' ? ', Kroatien' : language === 'hr' ? ', Hrvatska' : ', Croatia'),
			bio:
				((profileData?.bio || '') as string).trim() === 'I do everything from planting to building.'
					? copy(
						'I do everything from planting to building.',
						'Ich übernehme alles von der Bepflanzung bis zum Bau.',
						'Radim sve, od sadnje do izgradnje.'
					)
					: profileData?.bio ||
				copy(
					'Passionate about sustainable landscapes and empowering communities to grow thriving green spaces.',
					'Leidenschaftlich für nachhaltige Landschaften und dafür, Gemeinschaften beim Aufbau grüner Lebensräume zu stärken.',
					'Strastvena za održive krajobraze i osnaživanje zajednica za stvaranje uspješnih zelenih prostora.'
				),
			interests: [
				copy('Urban farming', 'Urban Farming', 'Urbana poljoprivreda'),
				copy('Native plants', 'Einheimische Pflanzen', 'Autohtone biljke'),
				copy('Community outreach', 'Arbeit mit der Gemeinschaft', 'Rad sa zajednicom')
			],
			skills: Array.isArray(profileData?.skills) ? profileData.skills : [],
			phone: profileData?.phone || null,
			profilePicture: normalizeStoredImageRef(profileData?.profile_picture_url),
			emailVerified: profileData?.email_verified ?? user.emailVerified ?? false,
			emailVerifiedAt: profileData?.email_verified_at ?? user.emailVerifiedAt ?? null,
			verificationGraceExpiresAt: profileData?.verification_grace_expires_at ?? user.verificationGraceExpiresAt ?? null,
			isRestricted: profileData?.is_restricted ?? user.isRestricted ?? false
		};
	}, [user, profileData]);

	const verificationDeadlineLabel = useMemo(() => {
		if (!profile.verificationGraceExpiresAt) return null;
		const date = new Date(profile.verificationGraceExpiresAt);
		if (Number.isNaN(date.getTime())) return null;
		return date.toLocaleDateString(language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
	}, [profile.verificationGraceExpiresAt]);

	if (!user) {
		return null;
	}

	const handleLogout = () => {
		logout();
		navigate('/', { replace: true });
	};

	const handleResendVerification = async () => {
		if (!user?.id || resendState === 'sending') return;
		setResendState('sending');
		setResendMessage(null);
		try {
			const result = await resendVerificationEmail(user.id);
			setResendState('sent');
			setResendMessage(result.message || 'Verification email sent.');
		} catch (err: any) {
			setResendState('error');
			setResendMessage(err?.message || 'Could not send verification email.');
		}
	};

	if (loading) {
		return (
			<div className="text-center py-5">
				<div className="spinner-border text-success" role="status">
					<span className="visually-hidden">{copy('Loading...', 'Lädt...', 'Učitavanje...')}</span>
				</div>
			</div>
		);
	}

	return (
		<div className="row g-4">
			<div className="col-12 col-lg-4">
				<div className="card text-center h-100">
					<div className="card-body d-flex flex-column">
						<div>
							{profile.profilePicture && !profileImageFailed ? (
								<img 
									src={getImageUrl(profile.profilePicture) || ''} 
									alt={profile.name} 
									className="rounded-circle mb-3"
									style={{ width: '120px', height: '120px', objectFit: 'cover' }}
									onError={() => setProfileImageFailed(true)}
								/>
							) : (
								<div className="display-6 mb-3">
									<i className="bi bi-person-circle"></i>
								</div>
							)}
							<h5 className="card-title">{profile.name}</h5>
							<p className="text-muted mb-1">{profile.role}</p>
							<p className="text-muted">{profile.location}</p>
							<hr />
							<div className="text-start small">
								<p className="mb-2">
									<span className="fw-semibold">{copy('Email:', 'E-Mail:', 'E-pošta:')}</span> {profile.email}
								</p>
								{profile.phone && (
									<p className="mb-2">
										<span className="fw-semibold">{copy('Phone:', 'Telefon:', 'Telefon:')}</span> {profile.phone}
									</p>
								)}
								<p className="mb-2">
									<span className="fw-semibold">{copy('Member since:', 'Mitglied seit:', 'Član od:')}</span> {profile.joined}
								</p>
								<p className="mb-0">
									<span className="fw-semibold">{copy('Email status:', 'E-Mail-Status:', 'Status e-pošte:')}</span>{' '}
									{profile.emailVerified ? (
										<span className="badge text-bg-success">{copy('Verified', 'Verifiziert', 'Potvrđeno')}</span>
									) : (
										<span className="badge text-bg-warning">{copy('Not verified', 'Nicht verifiziert', 'Nije potvrđeno')}</span>
									)}
								</p>
							</div>
						</div>
						<div className="mt-auto pt-3">
							<button type="button" className="btn btn-outline-danger w-100" onClick={handleLogout}>
								<i className="bi bi-box-arrow-right me-2"></i>
								{copy('Logout', 'Abmelden', 'Odjava')}
							</button>
						</div>
					</div>
				</div>
			</div>
			<div className="col-12 col-lg-8">
				<div className="card h-100">
					<div className="card-body">
						<h5 className="card-title mb-3">{copy('About', 'Über mich', 'O meni')}</h5>
						{!profile.emailVerified && (
							<div className={`alert ${profile.isRestricted ? 'alert-danger' : 'alert-warning'} d-flex align-items-start gap-3`} role="alert">
								<i className={`bi ${profile.isRestricted ? 'bi-exclamation-octagon-fill' : 'bi-exclamation-triangle-fill'} mt-1`}></i>
								<div className="flex-grow-1">
									<div className="fw-semibold mb-1">
										{profile.isRestricted
											? copy('Account is read-only until email verification', 'Konto ist schreibgeschützt bis zur E-Mail-Verifizierung', 'Račun je samo za čitanje do potvrde e-pošte')
											: copy('Please verify your email', 'Bitte bestätigen Sie Ihre E-Mail', 'Molimo potvrdite e-poštu')}
									</div>
									<div className="small mb-2">
										{profile.isRestricted
											? copy(
												'You can still view data, but edits are blocked until you verify your email.',
												'Sie können Daten weiterhin ansehen, aber Änderungen sind bis zur Verifizierung gesperrt.',
												'Podatke i dalje možete pregledavati, ali uređivanje je blokirano do potvrde e-pošte.'
											)
											: verificationDeadlineLabel
												? copy(
													`Verify your email by ${verificationDeadlineLabel} to avoid your account becoming read-only.`,
													`Bestätigen Sie Ihre E-Mail bis ${verificationDeadlineLabel}, damit Ihr Konto nicht schreibgeschützt wird.`,
													`Potvrdite e-poštu do ${verificationDeadlineLabel} kako račun ne bi postao samo za čitanje.`
												)
												: copy(
													'Verify your email to keep full access to your account.',
													'Bestätigen Sie Ihre E-Mail, um vollen Zugriff zu behalten.',
													'Potvrdite e-poštu kako biste zadržali puni pristup računu.'
												)}
									</div>
									<button
										type="button"
										className="btn btn-sm btn-success"
										onClick={handleResendVerification}
										disabled={resendState === 'sending'}
									>
										{resendState === 'sending'
											? copy('Sending...', 'Senden...', 'Slanje...')
											: copy('Resend verification email', 'Bestätigungs-E-Mail erneut senden', 'Ponovno pošalji potvrdu e-pošte')}
									</button>
									{resendMessage && (
										<div className={`small mt-2 ${resendState === 'error' ? 'text-danger' : 'text-success'}`}>
											{resendMessage}
										</div>
									)}
								</div>
							</div>
						)}
						<p className="text-muted">{profile.bio}</p>
						{profile.skills.length > 0 && (
							<>
								<hr />
								<h6 className="fw-semibold text-uppercase small text-muted mb-3">{copy('Skills', 'Fähigkeiten', 'Vještine')}</h6>
								<div className="d-flex flex-wrap gap-2">
									{profile.skills.map((skill: string) => (
										<span key={skill} className="badge text-bg-success-subtle text-success-emphasis">
											{getSkillLabel(skill, language)}
										</span>
									))}
								</div>
							</>
						)}
						<hr />
						<h6 className="fw-semibold text-uppercase small text-muted mb-3">{copy('Focus areas', 'Schwerpunkte', 'Područja fokusa')}</h6>
						<div className="d-flex flex-wrap gap-2">
							{profile.interests.map((interest) => (
								<span key={interest} className="badge text-bg-success-subtle text-success-emphasis">
									{interest}
								</span>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}


